import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RBHBox, RBHButton, RBHSelect, RBHSwitch, RBHTextInput } from '../../../../../shared/components';
import { Form, Select, Space } from 'antd';
import { FormattedMessage } from 'react-intl';
import { CommonValidator, httpCreators } from 'rbh-merchant-core';
import { mainCreators } from '../../../../main-action';
import { MAIN_STATE_KEY } from '../../../../main-reducer';
import { MainHelper } from '../../../../main-helper';
import { userSagas } from '../../redux/user-saga';
import { USER_REQUEST_ID_LIST } from '../../redux/user-constant';
import { UserService } from '../../redux/user-service';
import styles from './styles';
import styleText from '../../../../../shared/components/rbh-text/styles';
import styleButton from '../../../../../shared/components/rbh-button/styles';
import { User } from '../../../../../shared/models/user';
import { USER_STATE_KEY, userStateSelector } from '../../redux/user-reducer';
import { userCreators } from '../../redux/user-action';
import { useHistory } from 'react-router';
import { generalCreators } from '../../../../../redux/general/general-action';
import { GENERAL_STATE_KEY } from '../../../../../redux/general/general-reducer';
import { MODAL_TYPE } from '../../../../../redux/general/general-constant';

const { Option } = Select;

export const FormAddEdit: React.FC<Props> = (p) => {
  const history = useHistory();
  let user;
  if (p.editData) {
    user = new User(p.editData);
  }
  const dispatch = useDispatch();
  const userStatusCode = MainHelper.getMasterDataByScheme('MerchantUserStatus');
  const [userStatus, setUserStatus] = useState(user ? user.merchantUserStatus === userStatusCode[0] ? true : false : true);
  const [formInvalid, setFormInvalid] = useState(user ? false : true);

  const [form] = Form.useForm();
  const filteredShop = useSelector(userStateSelector(USER_STATE_KEY.BRANCH_FILTER_LIST)) as [];
  const branchDefaultId = useSelector(userStateSelector(USER_STATE_KEY.BRANCH_FILTER_DEFAULT_ID));

  useEffect(() => {
    dispatch(mainCreators.setMainState(MAIN_STATE_KEY.HEADER_MENU, 2));
    dispatch(userCreators.callFilterBranch(p.editData ? new User(p.editData) : undefined));
    return () => {
      if (history.action === 'POP' && p.editData) {
        history.push('/main/usermanagement');
      }
    };
  }, [history]);

  const layout = {
    labelCol: { span: 10 },
    wrapperCol: { span: 16 },
  };

  const button = {
    wrapperCol: { offset: 10, span: 16 },
  };

  const getBranchOption = () => {
    if (filteredShop && filteredShop.length > 0) {
      return filteredShop.map((shop: any, index) => {
        let label = shop.shopName;
        if (shop.branchCode) {
          label = shop.branchCode + ' - ' + label;
        }

        return (
          <Option style={styleText.formText} key={index} value={shop.shopId} title={label}>{label}</Option>
        );
      });
    }

    return null;
  };

  const checkFormInvalid = () => {
    // It's the expected behavior that this function is called 3 times when field is changed.
    // Please see https://ant.design/components/form/#FAQ
    // I used "onFieldsChange" instead of "onValuesChange"
    // because when this function is called by onValuesChange the validating is not done yet
    // and we will get the incorrect value when calling form.getFieldsError();
    // If there is a performance issue in the future because this function is called to many time
    // we can change to use onValuesChange and use setTimeout(() => {}, 0) to wrap the entire code in this function.

    let invalid = false;

    // Check that every field has value
    const values = form.getFieldsValue();
    for (const key in values) {
      if (values[key] === undefined) {
        invalid = true;
        break;
      }
    }

    if (!invalid) {
      // Every fields have values
      // So, check if there is error
      const errors = form.getFieldsError();
      for (const field of errors) {
        if (field.errors.length > 0) {
          invalid = true;
          break;
        }
      }
    }

    setFormInvalid(invalid);
  };

  const onSubmit = () => {
    const formdata = form.getFieldsValue();
    const data = {
      managerName: formdata.managerName,
      merchantUserEmail: formdata.merchantUserEmail,
      merchantUserMobileNo: formdata.merchantUserMobileNo,
      merchantUserStatus: formdata.merchantUserStatus ? userStatusCode[0] : userStatusCode[1],
      shopId: Number(formdata.shopId),
      merchantUserId: p.editData ? Number(p.editData.merchantUserId) : null
    };

    if (p.editData) {
      dispatch(httpCreators.request({
        id: USER_REQUEST_ID_LIST.EDIT_USER,
        sagaHandler: userSagas.editUserHandler as any,
        callbackParams: p.onSuccessCallback,
        service: UserService.editUser,
        serviceParams: data
      }));
    } else {
      dispatch(httpCreators.request({
        id: USER_REQUEST_ID_LIST.ADD_USER,
        sagaHandler: userSagas.addUserHandler as any,
        callbackParams: p.onSuccessCallback,
        service: UserService.addUser,
        serviceParams: data
      }));
    }
  };

  const trimFieldValue = (fieldName: string) => {
    const value = form.getFieldValue(fieldName);

    if (value) {
      form.setFieldsValue({
        [fieldName]: value.trim()
      });
      form.validateFields([fieldName]);
    }
  };

  const dispatchModal = (path: any) => {
    dispatch(generalCreators.setGeneralState(GENERAL_STATE_KEY.MODAL, {
      visible: true,
      onOk: () => history.push(path),
      type: MODAL_TYPE.CONFIRM_EDIT
    }));
  };

  return (
    <RBHBox>
      <Form
        form={form}
        size='large'
        style={styleText.formText}
        {...layout}
        initialValues={{ merchantUserStatus: userStatus }}
        onFieldsChange={checkFormInvalid}
      >
        {!filteredShop && user ? null : <Form.Item colon={false} required={true} label={<FormattedMessage id={'main.user.manager.branch'} />}>
          <Form.Item
            noStyle
            name='shopId'
            initialValue={user ? branchDefaultId : undefined}
            rules={[
              { required: true }
            ]}
          >
            <RBHSelect optionFilterProp='children' showSearch placeholder='main.choosebranch'>
              {getBranchOption()}
            </RBHSelect>
          </Form.Item>
          <span id='add-branch-link' style={styles.addBranchText} onClick={() => dispatchModal('/main/addbranch')}>
            <span>+ </span>
            <span style={styles.addBranchTextInner}><FormattedMessage id={'main.addbranch'} /></span>
          </span>
        </Form.Item>}
        <Form.Item
          name='managerName'
          initialValue={user ? user.managerName : undefined}
          colon={false}
          rules={[
            { required: true, message: <FormattedMessage id={'main.form.required'} /> },
            { pattern: /^[\u0E00-\u0E7Fa-zA-Z\- ().]*$/, message: <FormattedMessage id={'main.form.name.valid'} /> }
          ]}
          validateTrigger={['onChange', 'onBlur']}
          label={<FormattedMessage id={'main.user.manager.name'} />}
        >
          <RBHTextInput maxLength={50} placeholder={'main.user.form.add.manager.name'} onBlur={() => trimFieldValue('managerName')} />
        </Form.Item>
        <Form.Item
          name='merchantUserMobileNo'
          initialValue={user ? user.merchantUserMobileNo : undefined}
          colon={false}
          rules={[
            { required: true, message: <FormattedMessage id={'main.form.required'} /> },
            { pattern: CommonValidator.PhoneNumber.regexPattern, message: <FormattedMessage id={'main.inline.validate.phonenumber'} /> }
          ]}
          validateTrigger={['onChange', 'onBlur']}
          label={<FormattedMessage id={'main.user.manager.mobilenumber'} />}
        >
          <RBHTextInput maxLength={10} placeholder={'main.user.form.add.manager.mobilenumber'}
            onBlur={() => trimFieldValue('merchantUserMobileNo')} />
        </Form.Item>
        <Form.Item
          id='role-email-123'
          name='merchantUserEmail'
          initialValue={user ? user.merchantUserEmail : undefined}
          colon={false}
          rules={[
            { required: true, message: <FormattedMessage id={'main.form.required'} /> },
            {
              pattern: CommonValidator.Email.regexPattern,
              message: <FormattedMessage id={'main.login.email.format.valid'} />
            }
          ]}
          validateTrigger={['onChange', 'onBlur']}
          label={<FormattedMessage id={'main.user.manager.email'} />}
        >
          <RBHTextInput maxLength={50} placeholder={'main.user.form.add.manager.email'} onBlur={() => trimFieldValue('merchantUserEmail')} />
        </Form.Item>
        <Form.Item colon={false} required={true} label={<FormattedMessage id={'main.user.status'} />}>
          <Form.Item
            noStyle
            name='merchantUserStatus'
            valuePropName='checked'
          >
            <RBHSwitch defaultChecked={userStatus} size='small' onChange={setUserStatus} />
          </Form.Item>
          <span style={styles.statusWrapper}>
            {userStatus ? MainHelper.getMasterDataByCode(userStatusCode[0]) : MainHelper.getMasterDataByCode(userStatusCode[1])}
          </span>
        </Form.Item>
        <Form.Item {...button}>
          <Space size={'middle'}>
            <RBHButton id='cancel-add-user-button' text={'main.button_cancel'} style={styleButton.buttonFromCancelGradient}
              onClick={() => p.onCancel('/main/usermanagement')} />
            {user ? <RBHButton id='edit-user-button' text={'main.branchform.save'}
              style={styleButton.buttonFromConfirmGradient} disabled={formInvalid} onClick={onSubmit} /> :
              <RBHButton id='add-user-button' text={'main.vat_vatadd'}
                style={styleButton.buttonFromConfirmGradient} disabled={formInvalid} onClick={onSubmit} />}
          </Space>
        </Form.Item>
      </Form>
    </RBHBox>
  );
};

type Props = {
  editData?: any
  onSuccessCallback: any,
  onCancel?: any
};
